/**
 * Recipe-book logic (DOM-free, smoke-tested): categories, grid-fit detection, ingredient
 * lists, auto-fill planning, and the read-only non-crafting recipe lists (smelting, brewing,
 * smithing). Everything derives from the live registries (CRAFTING_RECIPES etc.), so recipes
 * added by other modules appear automatically.
 */

import { ITEM_BASE, nameOf } from '../items';
import { CRAFTING_RECIPES, type Ingredient, type Recipe } from './recipes';
import { FUNCTIONAL } from './creativeCatalog';
import { itemDef, maxStackOf } from './itemdb';
import { ALL_ITEM_IDS, I } from './itemIds';
import { smeltRecipes } from './smelting';
import { smithResult } from './smithing';
import { BREW_MIXES, potionByKey, potionName } from './brewing/potions';
import { cloneStack, stackOf, type Stack } from './types';

export type Category = 'construction' | 'equipment' | 'items' | 'nature';
export const CATEGORIES: readonly Category[] = ['construction', 'equipment', 'items', 'nature'];
export const CATEGORY_LABEL: Record<Category, string> = {
  construction: 'Construction', equipment: 'Equipment', items: 'Items', nature: 'Nature',
};

const EQUIP_NAME = /sword|pickaxe|axe$|shovel|hoe$|helmet|chestplate|leggings|boots|^bow$|crossbow|arrow|shield|shears|fishing rod|trident|mace|bucket|compass|clock|spyglass|flint and steel|saddle|carrot on a stick|elytra|brush/i;
const NATURE_NAME = /sapling|flower|tulip|orchid|allium|bluet|daisy|cornflower|lily|poppy|dandelion|sunflower|lilac|peony|azalea|petals|moss|hay|melon|pumpkin|mushroom|kelp|bamboo|sugar cane|cactus|log$|wood$|stem$|seeds|bone meal|dye$|wheat|bone block/i;

export function categoryOf(result: Stack): Category {
  const id = result.id;
  const name = nameOf(id) || '';
  if (id >= ITEM_BASE) {
    const d = itemDef(id);
    if (d.tool || d.armor || EQUIP_NAME.test(name)) return 'equipment';
    if (NATURE_NAME.test(name)) return 'nature';
    return 'items';
  }
  if (FUNCTIONAL.test(name)) return 'items';
  if (NATURE_NAME.test(name)) return 'nature';
  return 'construction';
}

export interface RecipeEntry {
  index: number;
  recipe: Recipe;
  category: Category;
  name: string;
  /** Lower-cased search text: result name plus every ingredient name. */
  search: string;
  /** Smallest square crafting grid the recipe fits (2 or 3), 0 = does not fit even 3x3. */
  size: 2 | 3 | 0;
}

export const ingredientIds = (ing: Ingredient): readonly number[] => (typeof ing === 'number' ? (ing > 0 ? [ing] : []) : ing);

/** Does the recipe fit a gw x gh crafting grid (shaped bounds / shapeless count)? */
export function fitsGrid(r: Recipe, gw: number, gh: number): boolean {
  if (r.shaped) return r.w <= gw && r.h <= gh;
  return r.ing.length <= gw * gh;
}

let entries: RecipeEntry[] | null = null;
let seen = -1;
/** Every crafting recipe (no unlock gating), rebuilt if the recipe table grew. */
export function allRecipes(): RecipeEntry[] {
  if (entries && seen === CRAFTING_RECIPES.length) return entries;
  entries = CRAFTING_RECIPES.map((recipe, index): RecipeEntry => {
    const name = nameOf(recipe.result.id) || 'Item';
    const ings = new Set<string>();
    for (const g of recipe.ing) for (const id of ingredientIds(g)) ings.add(nameOf(id));
    return {
      index, recipe, name, category: categoryOf(recipe.result),
      search: (name + ' ' + [...ings].join(' ')).toLowerCase(),
      size: fitsGrid(recipe, 2, 2) ? 2 : fitsGrid(recipe, 3, 3) ? 3 : 0,
    };
  });
  seen = CRAFTING_RECIPES.length;
  return entries;
}

/** Label for an ingredient: an item name, or "Planks (any)" style for tags. */
export function ingredientLabel(ing: Ingredient): string {
  const ids = ingredientIds(ing);
  if (ids.length === 0) return '';
  if (ids.length === 1) return nameOf(ids[0]);
  const names = ids.map((i) => nameOf(i));
  const last = (n: string): string => n.split(' ').pop() ?? n;
  if (names.every((n) => last(n) === last(names[0]))) return `${last(names[0])} (any)`;
  return `${names[0]} (any of ${names.length})`;
}

/** Ingredient lines for the tooltip: "3x Oak Planks (any)". */
export function ingredientLines(r: Recipe): string[] {
  const order: string[] = [];
  const count = new Map<string, number>();
  const label = new Map<string, string>();
  for (const g of r.ing) {
    if (g === 0) continue;
    const key = typeof g === 'number' ? String(g) : g.join(',');
    if (!count.has(key)) { order.push(key); label.set(key, ingredientLabel(g)); }
    count.set(key, (count.get(key) ?? 0) + 1);
  }
  return order.map((k) => `${count.get(k)}x ${label.get(k)}`);
}

// --- auto-fill --------------------------------------------------------------------------

/** Per-cell item ids (0 = empty), row-major over the gw x gh grid. */
export interface FillPlan { cells: number[] }

/**
 * Pick one concrete item per ingredient cell from `avail` (id -> count), top-left aligned.
 * Alternatives prefer whichever the player holds most of. Null = not craftable here.
 */
export function planFill(r: Recipe, gw: number, gh: number, avail: ReadonlyMap<number, number>): FillPlan | null {
  if (!fitsGrid(r, gw, gh)) return null;
  const left = new Map(avail);
  const cells = new Array<number>(gw * gh).fill(0);
  const place = (cell: number, ing: Ingredient): boolean => {
    const ids = ingredientIds(ing);
    if (ids.length === 0) return true;
    let best = 0, bestN = 0;
    for (const id of ids) { const n = left.get(id) ?? 0; if (n > bestN) { best = id; bestN = n; } }
    if (!best) return false;
    left.set(best, bestN - 1);
    cells[cell] = best;
    return true;
  };
  if (r.shaped) {
    for (let y = 0; y < r.h; y++) for (let x = 0; x < r.w; x++) {
      if (!place(y * gw + x, r.ing[y * r.w + x])) return null;
    }
  } else {
    for (let i = 0; i < r.ing.length; i++) if (!place(i, r.ing[i])) return null;
  }
  return { cells };
}

/** How many copies per cell a plan can be multiplied to (>= 1), capped by stack sizes. */
export function planMultiplier(plan: FillPlan, avail: ReadonlyMap<number, number>): number {
  const demand = new Map<number, number>();
  for (const id of plan.cells) if (id) demand.set(id, (demand.get(id) ?? 0) + 1);
  let k = 64;
  for (const [id, n] of demand) k = Math.min(k, Math.floor((avail.get(id) ?? 0) / n), maxStackOf(id));
  return Math.max(1, k);
}

/** id -> total count over some stacks. */
export function countStacks(stacks: Iterable<Stack>, into = new Map<number, number>()): Map<number, number> {
  for (const s of stacks) if (s.id > 0 && s.count > 0) into.set(s.id, (into.get(s.id) ?? 0) + s.count);
  return into;
}

// --- read-only non-crafting recipe lists ----------------------------------------------

export interface ListRow { inputs: Stack[]; output: Stack; note?: string }

export function smeltingRows(): ListRow[] {
  return smeltRecipes().map(([input, out]) => ({ inputs: [stackOf(input)], output: cloneStack(out) }));
}

export function brewingRows(): ListRow[] {
  const rows: ListRow[] = [];
  const bottleOf = (key: string): Stack => ({ id: I.POTION, count: 1, dmg: potionByKey(key).index });
  for (const [ing, from, to] of BREW_MIXES) {
    rows.push({ inputs: [stackOf(ing), bottleOf(from)], output: bottleOf(to) });
  }
  const water = bottleOf('water');
  rows.push({ inputs: [stackOf(I.GUNPOWDER), { ...water }], output: { ...water, id: I.SPLASH_POTION }, note: 'Any potion: splash' });
  rows.push({ inputs: [stackOf(I.DRAGON_BREATH), { ...water, id: I.SPLASH_POTION }], output: { ...water, id: I.LINGERING_POTION }, note: 'Any splash potion: lingering' });
  return rows;
}

export function brewingRowName(row: ListRow): string {
  return `${nameOf(row.inputs[0].id)} + ${potionName(row.inputs[1].id, row.inputs[1].dmg)} = ${potionName(row.output.id, row.output.dmg)}`;
}

export function smithingRows(): ListRow[] {
  const rows: ListRow[] = [];
  const tpl = stackOf(I.NETHERITE_UPGRADE_SMITHING_TEMPLATE), ingot = stackOf(I.NETHERITE_INGOT);
  for (const id of ALL_ITEM_IDS) {
    const base = stackOf(id);
    const out = smithResult(tpl, base, ingot);
    if (out) rows.push({ inputs: [cloneStack(tpl), base, cloneStack(ingot)], output: out });
  }
  return rows;
}

